import React from 'react';
import { IconButtonProps, IconButtonVariant } from '../../../types/DesignSystem/IconButton';
import { FOCUS_RING } from '../focusRing';
import { CONTROL_HEIGHT, resolveControlSize } from '../controlSize';
import { ControlSize } from '../../../types/DesignSystem/controlSize';
import Spinner from '../Spinner/Spinner';
import { useControlSize } from '../DensityProvider/DensityProvider';
import { withClassName } from '../cn';
import Tooltip from '../Tooltip/Tooltip';

/**
 * IconButton (§ 212) — see the type file for what it replaces (the SVG-in-a-
 * clickable-div population). A real `<button>`: focus ring, disabled semantics,
 * a REQUIRED accessible name, and a square hit target on the control grid —
 * `sm` (28px) already clears WCAG 2.5.8's 24px minimum, which the bare 16px
 * glyphs it replaces never did.
 */

/* The square's width — CONTROL_HEIGHT's values, stated as literals because the
   scanner cannot see a template (`tw-w-` + size would purge). */
const SQUARE_W: Record<ControlSize, string> = {
  sm: 'tw-w-7',
  md: 'tw-w-9',
  lg: 'tw-w-11',
};

const variantStyles: Record<IconButtonVariant, string> = {
  /* The § 65 subtle-button reasoning: ink, not accent — a toolbar of glyphs in
     accent blue would read as a row of links. */
  subtle: 'tw-bg-transparent tw-text-ink-secondary hover:tw-bg-surface-hover-strong hover:tw-text-ink',
  secondary: 'tw-bg-surface tw-border tw-border-edge-strong tw-text-ink-secondary hover:tw-bg-surface-hover hover:tw-text-ink',
  /* Quiet until hovered, then unmistakably the danger pair — the row delete. */
  'danger-subtle': 'tw-bg-transparent tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint',
};

/* Disabled follows the variant's shape (the Button arrangement): the bordered
   variant keeps a border, the ghosts stay ghosts. */
const disabledStyles: Record<IconButtonVariant, string> = {
  subtle: 'tw-bg-transparent tw-text-ink-disabled',
  secondary: 'tw-bg-surface-sunken tw-border tw-border-edge tw-text-ink-disabled',
  'danger-subtle': 'tw-bg-transparent tw-text-ink-disabled',
};

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({
  children,
  variant = 'subtle',
  size: sizeProp,
  disabled = false,
  loading = false,
  tooltip = false,
  tooltipPlacement = 'top',
  className = '',
  type = 'button',
  'data-testid': dataTestId,
  onClick,
  'aria-disabled': ariaDisabledProp,
  ...rest
}, ref) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  /* § 334 — a LOADING icon button stays focusable, as Button does since § 330: native
     `disabled` would throw a focused control's focus to <body>. It renders `aria-disabled`
     and swallows the click (which also stops a `type="submit"`). An explicit `disabled`
     is still the native attribute; a caller's own `aria-disabled` is kept. */
  const inertWhileLoading = loading && !disabled;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (inertWhileLoading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  /*
    § 303 — loading keeps the variant's RESTING shape with the hover rules off (a disabled
    element still matches :hover, and a busy control must not light up under the pointer);
    `disabled` outranks it for the shape, exactly as on Button. The click is blocked
    natively either way.
  */
  const resting = variantStyles[variant].split(/\s+/).filter((c) => !c.startsWith('hover:')).join(' ');
  const stateStyle = disabled
    ? `${disabledStyles[variant]} ${loading ? 'tw-cursor-wait' : 'tw-cursor-not-allowed'}`
    : loading
      ? `${resting} tw-cursor-wait`
      : variantStyles[variant];
  const button = (
    <button
      /* rest first — the Button.tsx rule. `aria-label` rides in rest, and the
         type makes it required, so it always lands on the element. */
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      aria-disabled={inertWhileLoading || ariaDisabledProp}
      onClick={handleClick}
      aria-busy={loading || undefined}
      data-testid={dataTestId}
      className={withClassName(`tw-inline-flex tw-items-center tw-justify-center tw-shrink-0 tw-rounded-ctl ${CONTROL_HEIGHT[size]} ${SQUARE_W[size]}
        tw-transition-all tw-duration-fast tw-ease-swift ${loading ? '' : 'motion-safe:active:tw-scale-[0.98]'} ${FOCUS_RING}
        ${stateStyle}`, className)}
    >
      {/* The spinner TAKES the glyph's place — its sr-only "Loading" joins the button's
          name, which is the announcement. `sm` (16px) in every square: the glyphs these
          buttons hold are 16–20px, and Button uses the same arc in its icon slot. */}
      {loading ? <Spinner size="sm" data-testid={dataTestId ? `${dataTestId}-spinner` : undefined} /> : children}
    </button>
  );
  if (!tooltip) return button;
  /* § 388 — the label, shown. `describeChild={false}`: it is already the button's name.
     No line-box fix on the wrapper (InfoTooltip's § 198 one): measured in Chromium, a flex
     toolbar and a plain block both hold the wrapped button at exactly its own 36px, because
     the centred glyph gives the square a mid baseline (`composition.pw.ts` keeps that true). */
  return (
    <Tooltip
      content={rest['aria-label']}
      placement={tooltipPlacement}
      describeChild={false}
      /* The wrapper `${testid}-tooltip`, its bubble `${testid}-tooltip-bubble` — derived, so two
         icon buttons never share one. */
      data-testid={dataTestId ? `${dataTestId}-tooltip` : undefined}
    >
      {button}
    </Tooltip>
  );
});

export default IconButton;
